import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { ArrowLeft, Building2, CheckCircle, Shield } from 'lucide-react'
import Select from '../../../components/Select'

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
]

export default function BankVerificationPage() {
  const navigate = useNavigate()
  const { applicationId } = useParams()
  const [searchParams] = useSearchParams()
  const isUpdate = !!searchParams.get('update')
  const isRefundIssued = !!searchParams.get('refundIssued')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [existingVerification, setExistingVerification] = useState(null)
  const [error, setError] = useState('')

  const [bankName, setBankName] = useState('')
  const [accountName, setAccountName] = useState('')
  const [accountType, setAccountType] = useState('checking')
  const [accountHolderType, setAccountHolderType] = useState('business')
  const [accountNumber, setAccountNumber] = useState('')
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [address1, setAddress1] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')

  const [isOnBehalf, setIsOnBehalf] = useState(false)
  const [onBehalfName, setOnBehalfName] = useState('')
  const [onBehalfTitle, setOnBehalfTitle] = useState('')
  const [onBehalfPhone, setOnBehalfPhone] = useState('')
  const [onBehalfEmail, setOnBehalfEmail] = useState('')

  const isOnBehalfValid = !isOnBehalf || (
    onBehalfName.trim() &&
    onBehalfTitle.trim() &&
    onBehalfPhone.trim() &&
    onBehalfEmail.trim()
  )

  const isFormValid =
    bankName.trim() &&
    accountName.trim() &&
    accountType &&
    accountNumber.trim() &&
    confirmAccountNumber.trim() &&
    accountNumber === confirmAccountNumber &&
    /^\d{9}$/.test(routingNumber) &&
    firstName.trim() &&
    lastName.trim() &&
    address1.trim() &&
    city.trim() &&
    state &&
    zip.trim() &&
    isOnBehalfValid

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await fetch(`${apiURL}/api/applications/${applicationId}/bank-verification`, {
          headers: { 'Authorization': account.accessToken }
        })
        const data = await res.json()

        if (data.response.error) {
          if (data.response.error === 'Failed To Authenticate Token.') {
            const refreshedToken = await refreshAccessToken()
            if (refreshedToken && refreshedToken.length > 0) return fetchExisting()
            else return navigate('/', { state: { expired: true } })
          }
        } else if (data.response.bankVerification) {
          setExistingVerification(data.response.bankVerification)
          // Automatically proceed to ACH Agreement if banking is already on file and this isn't an explicit update/refund request
          if (!isUpdate && !isRefundIssued) {
            navigate(`/applications/${applicationId}/ach-agreement`)
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchExisting()
  }, [applicationId])

  useEffect(() => {
    if (existingVerification) {
      setBankName(existingVerification.bankName || '')
      setAccountName(existingVerification.accountName || '')
      setAccountType(existingVerification.accountType || 'checking')
      setFirstName(existingVerification.firstName || '')
      setLastName(existingVerification.lastName || '')
      setAddress1(existingVerification.address1 || '')
      setCity(existingVerification.city || '')
      setState(existingVerification.state || '')
      setZip(existingVerification.zip || '')
      setIsOnBehalf(!!existingVerification.isOnBehalf)
      setOnBehalfName(existingVerification.authorizerName || '')
      setOnBehalfTitle(existingVerification.authorizerTitle || '')
      setOnBehalfPhone(existingVerification.authorizerPhone || '')
      setOnBehalfEmail(existingVerification.authorizerEmail || '')
    }
  }, [existingVerification])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/bank-verification`, {
        method: 'POST',
        headers: {
          'Authorization': account.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bankName: bankName.trim(),
          accountName: accountName.trim(),
          accountType,
          accountHolderType,
          accountNumber: accountNumber.trim(),
          routingNumber: routingNumber.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          address1: address1.trim(),
          city: city.trim(),
          state,
          zip: zip.trim(),
          isOnBehalf,
          ...(isOnBehalf && {
            onBehalfName: onBehalfName.trim(),
            onBehalfTitle: onBehalfTitle.trim(),
            onBehalfPhone: onBehalfPhone.trim(),
            onBehalfEmail: onBehalfEmail.trim()
          })
        })
      })

      const data = await res.json()

      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && refreshedToken.length > 0)
            return handleSubmit(e)
          else
            return navigate('/', { state: { expired: true } })
        }
        setError(data.response.error)
      } else {
        setIsSubmitted(true)
        setExistingVerification(data.response.bankVerification)

        if (isUpdate)
          navigate(`/applications/${applicationId}/ach-agreement?update=1`)
        else if (isRefundIssued)
          navigate(`/applications/${applicationId}/ach-agreement?refundIssued=1`)
        else
          navigate(`/applications/${applicationId}/ach-agreement`)
      }
    } catch {
      setError('Something went wrong. Please try again.')
    }

    setIsSubmitting(false)
  }

  return (
    <div className='w-full min-h-full flex flex-col items-center p-6 pb-16 text-slate-900 dark:text-white transition-colors duration-300'>
      <div className='w-full flex flex-col items-center mt-4'>

        {/* Back Navigation */}
        <button
          onClick={() => navigate(`/applications/${applicationId}/dashboard`)}
          className='self-start flex items-center gap-2 text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors mb-6 cursor-pointer'
        >
          <ArrowLeft className='w-4 h-4' />
          <span className='text-sm'>Back to Dashboard</span>
        </button>

        {isLoading ? (
          <div className='w-full flex flex-col items-center gap-4 mt-8'>
            <div className='h-8 w-64 bg-gray-200 dark:bg-slate-700 rounded animate-pulse' />
            <div className='h-4 w-96 bg-gray-200 dark:bg-slate-700 rounded animate-pulse' />
            <div className='w-full border-2 border-gray-200 dark:border-slate-700 rounded-xl p-8 space-y-4 mt-4'>
              {[...Array(6)].map((_, i) => (
                <div key={i} className='h-12 w-full bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse' />
              ))}
            </div>
          </div>
        ) : (isSubmitted || existingVerification) && !isUpdate && !isRefundIssued ? (
          /* Success / Existing State */
          <div className='w-full flex flex-col items-center'>
            <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-8 text-center transition-colors duration-300'>
              <div className='flex justify-center mb-6'>
                <CheckCircle className='w-16 h-16 text-green-600 dark:text-green-500' />
              </div>
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-3'>
                {isSubmitted ? 'Bank Account Verified' : 'Bank Verification on File'}
              </h2>
              <p className='text-gray-600 dark:text-slate-300 mb-6'>
                {isSubmitted
                  ? 'Your bank account information has been securely verified and saved.'
                  : 'You have already submitted your bank account information.'}
              </p>

              {/* Display stored info */}
              <div className='bg-gray-50 dark:bg-slate-700 rounded-lg p-5 text-left space-y-2 mb-6'>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-slate-400'>Bank Name</span>
                  <span className='text-sm font-medium text-gray-800 dark:text-slate-200'>{existingVerification.bankName}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-slate-400'>Account Name</span>
                  <span className='text-sm font-medium text-gray-800 dark:text-slate-200'>{existingVerification.accountName}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-slate-400'>Account Type</span>
                  <span className='text-sm font-medium text-gray-800 dark:text-slate-200 capitalize'>{existingVerification.accountType}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-slate-400'>Account Number</span>
                  <span className='text-sm font-medium text-gray-800 dark:text-slate-200'>{existingVerification.maskedAccountNumber}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-slate-400'>Routing Number</span>
                  <span className='text-sm font-medium text-gray-800 dark:text-slate-200'>{existingVerification.maskedRoutingNumber}</span>
                </div>
                <div className='pt-2 border-t border-gray-200 dark:border-slate-600 space-y-2'>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-500 dark:text-slate-400'>Billing Name</span>
                    <span className='text-sm font-medium text-gray-800 dark:text-slate-200'>{existingVerification.firstName} {existingVerification.lastName}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-gray-500 dark:text-slate-400'>Billing Address</span>
                    <span className='text-sm font-medium text-gray-800 dark:text-slate-200 text-right'>{existingVerification.address1}<br />{existingVerification.city}, {existingVerification.state} {existingVerification.zip}</span>
                  </div>
                </div>
              </div>

              <div className='flex flex-col sm:flex-row items-center gap-4 justify-center'>
                <button
                  onClick={() => navigate(`/applications/${applicationId}/dashboard`)}
                  className='w-full sm:w-auto py-3 px-8 border-2 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 font-semibold rounded-lg transition-colors cursor-pointer'
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => {
                    if (isUpdate)
                      navigate(`/applications/${applicationId}/ach-agreement?update=1`)
                    else if (isRefundIssued)
                      navigate(`/applications/${applicationId}/ach-agreement?refundIssued=1`)
                    else
                      navigate(`/applications/${applicationId}/ach-agreement`)
                  }}
                  className='w-full sm:w-auto py-3 px-8 bg-ertc-primary hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors cursor-pointer'
                >
                  Proceed to ACH Agreement
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Form State */
          <div className='w-full flex flex-col items-center'>
            {/* Header */}
            <div className='mb-6 text-center'>
              <div className='flex justify-center mb-4'>
                <div className='p-3 bg-blue-50 dark:bg-slate-800 rounded-full'>
                  <Building2 className='w-8 h-8 text-blue-600 dark:text-blue-400' />
                </div>
              </div>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                Bank Account Verification
              </h1>
              <p className='text-gray-600 dark:text-slate-300 max-w-md mx-auto'>
                Please provide your bank account details. This information will be securely stored for future disbursements.
              </p>
            </div>

            {/* Security Notice */}
            <div className='w-full flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg px-4 py-3 mb-6 transition-colors duration-300'>
              <Shield className='w-5 h-5 text-green-600 dark:text-green-500 flex-shrink-0' />
              <p className='text-sm text-green-800 dark:text-green-400'>
                Your information is encrypted with AES-256 and securely stored. Full account and routing numbers are never stored in plain text.
              </p>
            </div>

            <form onSubmit={handleSubmit} className='w-full space-y-6'>
              {/* Bank Information Section */}
              <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4 transition-colors duration-300'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>Bank Information</h2>

                <div>
                  <label htmlFor='bank-name' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Bank Name</label>
                  <input
                    id='bank-name'
                    type='text'
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder='e.g. Chase, Bank of America'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                </div>

                <div>
                  <label htmlFor='account-name' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Account Holder Name</label>
                  <input
                    id='account-name'
                    type='text'
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder='Name on the bank account'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='account-holder-type' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Account Holder Type</label>
                    <Select
                      value={accountHolderType}
                      onChange={setAccountHolderType}
                      options={[
                        { value: 'business', label: 'Business' },
                        { value: 'personal', label: 'Personal' }
                      ]}
                    />
                  </div>
                  <div>
                    <label htmlFor='account-type' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Account Type</label>
                    <Select
                      value={accountType}
                      onChange={setAccountType}
                      options={[
                        { value: 'checking', label: 'Checking' },
                        { value: 'savings', label: 'Savings' }
                      ]}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor='routing-number' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Routing Number</label>
                  <input
                    id='routing-number'
                    type='text'
                    inputMode='numeric'
                    maxLength={9}
                    value={routingNumber}
                    onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder='9-digit routing number'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                  {routingNumber && !/^\d{9}$/.test(routingNumber) && (
                    <p className='text-xs text-red-500 mt-1'>Routing number must be exactly 9 digits</p>
                  )}
                </div>

                <div>
                  <label htmlFor='account-number' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Account Number</label>
                  <input
                    id='account-number'
                    type='text'
                    inputMode='numeric'
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder='Bank account number'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                </div>

                <div>
                  <label htmlFor='confirm-account-number' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Confirm Account Number</label>
                  <input
                    id='confirm-account-number'
                    type='text'
                    inputMode='numeric'
                    value={confirmAccountNumber}
                    onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder='Re-enter account number'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                  {confirmAccountNumber && accountNumber !== confirmAccountNumber && (
                    <p className='text-xs text-red-500 mt-1'>Account numbers do not match</p>
                  )}
                </div>
              </div>

              {/* Billing Address Section */}
              <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4 transition-colors duration-300'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>Billing Address</h2>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='first-name' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>First Name</label>
                    <input
                      id='first-name'
                      type='text'
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder='First name'
                      className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                    />
                  </div>
                  <div>
                    <label htmlFor='last-name' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Last Name</label>
                    <input
                      id='last-name'
                      type='text'
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder='Last name'
                      className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor='address-1' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Street Address</label>
                  <input
                    id='address-1'
                    type='text'
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder='123 Main St'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                  />
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                  <div>
                    <label htmlFor='city' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>City</label>
                    <input
                      id='city'
                      type='text'
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder='City'
                      className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                    />
                  </div>
                  <div>
                    <label htmlFor='state' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>State</label>
                    <Select
                      value={state}
                      onChange={setState}
                      options={US_STATES.map(s => ({ value: s, label: s }))}
                      placeholder='Select'
                    />
                  </div>
                  <div>
                    <label htmlFor='zip' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>ZIP Code</label>
                    <input
                      id='zip'
                      type='text'
                      inputMode='numeric'
                      maxLength={5}
                      value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                      placeholder='ZIP'
                      className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                    />
                  </div>
                </div>
              </div>

              {/* On Behalf Authorization Section */}
              <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4 transition-colors duration-300'>
                <label className='flex items-start gap-3 cursor-pointer select-none'>
                  <input
                    type='checkbox'
                    checked={isOnBehalf}
                    onChange={(e) => setIsOnBehalf(e.target.checked)}
                    className='mt-1 w-5 h-5 rounded border-gray-300 dark:border-slate-600 text-ertc-primary focus:ring-ertc-primary cursor-pointer accent-ertc-primary'
                  />
                  <div>
                    <span className='text-sm font-semibold text-gray-900 dark:text-white'>I am authorizing on behalf of client</span>
                    <p className='text-xs text-gray-500 dark:text-slate-400 mt-0.5'>Check this box if you are submitting this form as an authorized representative.</p>
                  </div>
                </label>

                {isOnBehalf && (
                  <div className='space-y-4 pt-2 border-t border-gray-100 dark:border-slate-700 mt-4'>
                    <h3 className='text-sm font-semibold text-gray-900 dark:text-white'>Authorized Representative Information</h3>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                      <div>
                        <label htmlFor='on-behalf-name' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Full Name</label>
                        <input
                          id='on-behalf-name'
                          type='text'
                          value={onBehalfName}
                          onChange={(e) => setOnBehalfName(e.target.value)}
                          placeholder='Your full name'
                          className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                        />
                      </div>
                      <div>
                        <label htmlFor='on-behalf-title' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Title</label>
                        <input
                          id='on-behalf-title'
                          type='text'
                          value={onBehalfTitle}
                          onChange={(e) => setOnBehalfTitle(e.target.value)}
                          placeholder='e.g. CPA, Attorney, Agent'
                          className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                      <div>
                        <label htmlFor='on-behalf-phone' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Phone Number</label>
                        <input
                          id='on-behalf-phone'
                          type='tel'
                          value={onBehalfPhone}
                          onChange={(e) => setOnBehalfPhone(e.target.value)}
                          placeholder='(555) 555-5555'
                          className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                        />
                      </div>
                      <div>
                        <label htmlFor='on-behalf-email' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>Email Address</label>
                        <input
                          id='on-behalf-email'
                          type='email'
                          value={onBehalfEmail}
                          onChange={(e) => setOnBehalfEmail(e.target.value)}
                          placeholder='email@example.com'
                          className='w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white dark:bg-slate-700 dark:text-white'
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Error State */}
              {error && (
                <div className='w-full bg-red-50 dark:bg-red-900/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm transition-colors duration-300'>
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type='submit'
                disabled={!isFormValid || isSubmitting}
                className='w-full py-4 bg-ertc-primary hover:bg-orange-600 disabled:bg-gray-400 dark:disabled:bg-slate-600 text-white font-semibold rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed text-lg'
              >
                {isSubmitting ? 'Verifying...' : 'Submit Bank Information'}
              </button>

              {/* Loading State */}
              {isSubmitting && (
                <div className='flex flex-row items-center justify-center gap-2 transition-colors duration-300'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
                  <p className='text-sm text-gray-500 dark:text-slate-400'>Securely verifying your bank account...</p>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
